// CPC LOCAL: CLOB orders & traders from Push Hub (replaces suix address metrics).

import { formatAmount } from '@mysten/core';
import { Heading, LoadingIndicator, Text } from '@mysten/ui';
import { ParentSize } from '@visx/responsive';
import clsx from 'clsx';
import { useMemo } from 'react';

import { AreaGraph } from '~/components/AreaGraph';
import { FormattedStatsAmount } from '~/components/HomeMetrics/FormattedStatsAmount';
import { ErrorBoundary } from '~/components/error-boundary/ErrorBoundary';
import { useCpcHomeStats, type CpcKlineVolumePoint } from '~/hooks/useCpcHomeStats';
import { Card } from '~/ui/Card';
import {
	buildKlineAxisTicks,
	formatKlineAxisTime,
	formatKlineTooltipTime,
} from '~/utils/cpcKlineChartFormat';

function TooltipContent({ data }: { data: CpcKlineVolumePoint }) {
	const dateFormatted = formatKlineTooltipTime(data.openTimeMs);
	return (
		<div className="flex flex-col gap-0.5">
			<Text variant="subtitleSmallExtra/medium" color="steel-darker">
				{dateFormatted}
			</Text>
			<Heading variant="heading6/semibold" color="steel-darker">
				{formatAmount(data.volumeLots)}
			</Heading>
			<Text variant="subtitleSmallExtra/medium" color="steel-darker" uppercase>
				Hourly trade volume (lots)
			</Text>
		</div>
	);
}

export function CpcMarketParticipantsGraph() {
	const stats = useCpcHomeStats();
	const chartSeries = stats.volumeSeries;
	const hasVolume = useMemo(
		() => chartSeries.some((p) => p.volumeLots > 0),
		[chartSeries],
	);
	const lastHourVolume = stats.lastHourVolume;

	return (
		<Card growOnHover bg="dark" spacing={!hasVolume ? 'lg' : 'lgGraph'} height="full">
			<div className="flex h-full flex-col gap-4 overflow-hidden">
				<div className="flex flex-col gap-1">
					<Heading variant="heading4/semibold" color="dark">
						CLOB Market
					</Heading>
					<Text variant="subtitleSmall/medium" color="steel">
						Orders, traders & hourly CLOB volume (Push Hub)
					</Text>
				</div>
				<div className="flex flex-wrap gap-6">
					<FormattedStatsAmount
						orientation="vertical"
						label="Total Orders"
						tooltip="All orders ever placed on the CLOB (Push Hub index)"
						amount={stats.totalOrders}
						size="md"
						color="dark"
					/>
					<FormattedStatsAmount
						orientation="vertical"
						label="Open Orders"
						tooltip="Orders still open or partially filled"
						amount={stats.openOrders}
						size="md"
						color="dark"
					/>
					<FormattedStatsAmount
						orientation="vertical"
						label="Traders (24h)"
						tooltip="Unique maker/taker addresses in the last 24 hours"
						amount={stats.uniqueTraders24h}
						size="md"
						color="dark"
					/>
					<FormattedStatsAmount
						orientation="vertical"
						label="Last Hour Vol."
						tooltip="CLOB trade volume in the latest 1h kline bucket (lots, not chain tx count)"
						amount={lastHourVolume}
						size="md"
						color="dark"
					/>
				</div>
				<div
					className={clsx(
						'flex min-h-[180px] flex-1 flex-col items-center justify-center rounded-xl transition-colors',
						!hasVolume && 'bg-white/10',
					)}
				>
					{stats.isLoading ? (
						<div className="flex flex-col items-center gap-1">
							<LoadingIndicator />
							<Text color="dark" variant="body/medium">
								loading Push Hub data
							</Text>
						</div>
					) : hasVolume ? (
						<div className="relative flex-1 self-stretch">
							<ErrorBoundary>
								<ParentSize className="absolute">
									{({ height, width }) => {
										const axis = buildKlineAxisTicks(
											chartSeries.map((p) => p.openTimeMs),
											width,
										);
										return (
											<AreaGraph
												data={chartSeries}
												height={height}
												width={width}
												getX={({ openTimeMs }) => openTimeMs}
												getY={({ volumeLots }) => volumeLots}
												color="blue"
												formatX={(ms) => formatKlineAxisTime(ms, axis.labelMode)}
												formatY={formatAmount}
												xTickValues={axis.tickValues}
												tooltipContent={TooltipContent}
											/>
										);
									}}
								</ParentSize>
							</ErrorBoundary>
						</div>
					) : (
						<Text color="dark" variant="body/medium">
							No recent CLOB activity in kline window
						</Text>
					)}
				</div>
			</div>
		</Card>
	);
}
